import React from 'react';
import { Button, Modal, useDevice } from '@tecma/ds';
import { useTranslation } from '@tecma/i18n';
import classNames from 'classnames';

import usePreventBodyScroll from 'hooks/usePreventBodyScroll';
import CardQuoteList from 'components/CardQuoteList/CardQuoteList';
import { QuoteDetailsContainer } from 'components/QuoteDetailsContainer';

import type { Quote } from 'types/quote';
import type { CardQuoteProps } from 'components/CardQuote/CardQuote';

import './quote-details-modal.scss';

interface QuoteDetailsModalRequiredProps {
  isOpen: boolean;
  onClose: () => void;
  getModalTitle: () => string;
  onQuoteDeleted: () => void;
}

// Optional Props
interface QuoteDetailsModalOptionalProps {
  selectedQuote?: Quote;
  quoteList?: CardQuoteProps[];
  getModalDescription?: () => string;
  isSendRequestFlow?: boolean;
  footerActionOnClose?: () => void;
  footerActionOnSubmit?: () => void;
}

// Combined required and optional props to build the full prop interface
export interface QuoteDetailsModalProps extends QuoteDetailsModalRequiredProps, QuoteDetailsModalOptionalProps {}

const QuoteDetailsModal: React.FC<QuoteDetailsModalProps> = ({
  isOpen,
  onClose,
  selectedQuote,
  quoteList,
  getModalTitle,
  getModalDescription,
  isSendRequestFlow,
  footerActionOnClose,
  footerActionOnSubmit,
  onQuoteDeleted,
}) => {
  const { t } = useTranslation();
  const device = useDevice();
  const isDesktop = device.type === 'desktop';

  usePreventBodyScroll(isOpen);

  const quoteDetailsModalClassList = classNames('quote-details-modal', {
    'full-screen': !isDesktop && quoteList && !selectedQuote,
    'send-request-flow': isSendRequestFlow,
  });

  const handleOnDeleteQuote = () => {
    onQuoteDeleted();
    onClose();
  };

  return (
    <Modal className={quoteDetailsModalClassList} isOpen={isOpen} onClose={onClose} closeOnBackDropClick disabledMobileAnimation>
      <Modal.Header onClose={onClose}>
        <span>{getModalTitle()}</span>
        <Button onClick={onClose} iconName={isDesktop ? 'x' : 'chevron-left'} color='transparent' />
      </Modal.Header>
      <Modal.Content>
        {getModalDescription && <span className='quote-details-modal-description'>{getModalDescription()}</span>}
        <div className='quote-details-modal-content'>
          {isDesktop ? (
            <>
              {quoteList && <CardQuoteList quotes={quoteList} selectedQuoteId={selectedQuote?.id} />}
              {selectedQuote && (
                <QuoteDetailsContainer
                  quote={selectedQuote}
                  footerActionOnClose={footerActionOnClose}
                  footerActionOnSubmit={footerActionOnSubmit}
                  onQuoteDeleted={handleOnDeleteQuote}
                />
              )}
            </>
          ) : quoteList && isSendRequestFlow ? (
            <>
              <CardQuoteList quotes={quoteList} selectedQuoteId={selectedQuote?.id} />
              <div className='mobile-footer-select-quote'>
                <Button className='select-configuration-button' fluid onClick={() => footerActionOnSubmit?.()} color='primary'>
                  {t('send-request.button-label.select-configuration')}
                </Button>
              </div>
            </>
          ) : quoteList && !selectedQuote ? (
            <CardQuoteList quotes={quoteList} />
          ) : (
            selectedQuote && <QuoteDetailsContainer quote={selectedQuote} onQuoteDeleted={handleOnDeleteQuote} />
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default React.memo(QuoteDetailsModal);
